import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import { showCurrency } from '../../utils';
import { AssetDeletionModal } from '../../components/CascadeDeletionModal';
import { Section, StatStrip, DataTable, IdChip, Pill, RowActionsMenu, Button, ExportModal, buildXlsxExport } from '../../components/EntityList';
import AssetEditor from '../Assets/AssetEditor';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AccountAssets({ account, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();

	// Sends the editor back to this exact tab on save/cancel instead of letting
	// it land on the newly created asset's detail page.
	const returnTo = () => encodeURIComponent(location.pathname + location.search);
	const [showEditorModal, setShowEditorModal] = useState(false);
	const [editingAsset, setEditingAsset] = useState(null);
	const [deletingAsset, setDeletingAsset] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	// The ID column shows a slice of the Mongo _id, which carries no meaning to
	// sort by — the asset's name is the identifier a user actually scans for.
	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'name',
		accessors: { name: (asset) => asset.name },
	});

	const closeEditorModal = () => {
		setShowEditorModal(false);
		setEditingAsset(null);
	};

	const stats = useMemo(() => {
		const enUso = rows.filter((asset) => !asset.sold);
		const valorByCurrency = new Map();
		enUso.forEach((asset) => {
			const key = asset.currency || '—';
			valorByCurrency.set(key, (valorByCurrency.get(key) || 0) + (asset.price || 0));
		});
		return [
			{ label: 'En uso', value: enUso.length },
			{
				label: 'Valor total',
				value: [...valorByCurrency.entries()].map(([currency, price]) => formatMoney(currency, price)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows]);


	const columns = [
		{ key: 'id', header: 'ID', render: (asset) => <IdChip>{asset._id?.slice(-6)}</IdChip> },
		{ key: 'name', header: 'Nombre', sortable: true, render: (asset) => asset.name },
		{ key: 'type', header: 'Categoría', render: (asset) => <Pill tone='neutral'>{asset.type}</Pill> },
		{
			key: 'price',
			header: 'Valor',
			className: 'tw:text-right',
			render: (asset) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(asset.currency, asset.price)}</span>,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (asset) => <Pill tone={asset.sold ? 'neutral' : 'ok'}>{asset.sold ? 'Vendido' : 'En uso'}</Pill>,
		},
	];

	const exportFields = [
		{ key: 'name', label: 'Nombre', locked: true },
		{ key: 'type', label: 'Categoría' },
		{ key: 'price', label: 'Valor' },
		{ key: 'estado', label: 'Estado' },
	];

	const exportGetters = {
		name: (asset) => asset.name || '',
		type: (asset) => asset.type || '',
		price: (asset) => formatMoney(asset.currency, asset.price),
		estado: (asset) => (asset.sold ? 'Vendido' : 'En uso'),
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Activos',
			filePrefix: 'activos',
		});

	const renderRowActions = (asset) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => { setEditingAsset(asset); setShowEditorModal(true); } },
				{ label: 'Eliminar', onClick: () => setDeletingAsset(asset), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={() => navigate(`/AdminScreen/accounts/account/${account._id}/newAsset?returnTo=${returnTo()}`)}>
					Nuevo activo
				</Button>
			</div>
			<Section title='Activos'>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(asset) => navigate(`/AdminScreen/assets/asset/${asset._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron activos para esta cuenta.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle='Activos de esta cuenta'
				onExport={handleExport}
			/>

			<Modal show={showEditorModal} onHide={closeEditorModal} size='lg' backdrop='static' centered>
				<Modal.Header closeButton>
					<Modal.Title>{editingAsset ? 'Editar Activo' : 'Nuevo Activo'}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<AssetEditor
						mode={editingAsset ? 'edit' : 'create'}
						initialData={editingAsset || null}
						onCancel={closeEditorModal}
						onSaved={() => {
							onRefetch();
							closeEditorModal();
						}}
						disableOwner={true}
						preloadOwner={account}
						saveLabel={editingAsset ? 'Guardar' : 'Agregar'}
					/>
				</Modal.Body>
			</Modal>

			<AssetDeletionModal
				show={Boolean(deletingAsset)}
				record={deletingAsset}
				onClose={() => setDeletingAsset(null)}
				onDeleted={() => {
					setDeletingAsset(null);
					onRefetch();
				}}
			/>
		</div>
	);
}

export default AccountAssets;
